/**
 * Two-factor authentication page — /2fa
 *
 * Shown after email/password verification when the user has 2FA (TOTP) enabled.
 * Better-Auth redirects here automatically after successful password auth if
 * the user has 2FA configured.
 *
 * Supports three modes:
 * - TOTP: Enter 6-digit code from authenticator app (default)
 * - OTP: Request the account's configured delivery channel
 * - Recovery: Enter a backup recovery code if the configured factor is unavailable
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@better-bookkeeping/ui/button";
import { Input } from "@better-bookkeeping/ui/input";
import { Alert, AlertDescription } from "@better-bookkeeping/ui/alert";
import { Checkbox } from "@better-bookkeeping/ui/checkbox";
import { AlertCircle, Key, Mail, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Label } from "@better-bookkeeping/ui/label";
import { authClient, orpcClient } from "@/lib/auth-client";
import { safePostAuthRedirect } from "@/lib/redirect-utils";
import { navigateToFinalDestination } from "@/lib/post-auth-redirect";
import { useRuntimeConfig } from "@/lib/runtime-config";
import { OtpDigits } from "@/components/otp-digits";
import { AuthLoading } from "@/components/auth-loading";
import { DuplicateAccountWarningModal } from "@/components/duplicate-account-warning-modal";
import { withInviteTokenSearch } from "@/lib/pending-pricing-invite";

const AUTO_SEND_DEDUPE_MS = 5_000;
const lastAutoSendAtByKey = new Map<string, number>();

export const Route = createFileRoute("/2fa")({
  validateSearch: withInviteTokenSearch({
    redirect_uri: z.string().optional(),
  }).parse,
  component: TwoFactorPage,
});

// Exported for unit tests, matching SecurityPage in ./security.
export function TwoFactorPage() {
  const { redirect_uri } = Route.useSearch();
  const navigate = useNavigate();
  const runtimeConfig = useRuntimeConfig();
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"detecting" | "totp" | "otp" | "recovery">("detecting");
  const [canUseOtpDelivery, setCanUseOtpDelivery] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  // AUTH-104: trust-device opt-in replaces the AUTH-103 login-page "Remember me"
  // checkbox. Only shown on the TOTP form — backup-code path (AUTH-51) forces
  // short session because a backup-code login is a "something is wrong" signal.
  // Uses Better Auth's native trustDevice parameter: sets 30d persistent cookie,
  // clears dont_remember, and skips 2FA prompts on future sign-ins (industry
  // norm — matches GitHub / Google / Microsoft / 1Password behavior).
  const [trustDevice, setTrustDevice] = useState(false);

  // AUTH-154: warn duplicate-flagged accounts BEFORE the 2FA challenge, not
  // after. No session exists yet, so this uses the cookie-authenticated
  // pre-2FA check (masked canonical email). Errors mean "couldn't check" —
  // no modal, and the post-auth safety net in navigateToFinalDestination
  // stays armed (skipDuplicateCheck only set on success).
  const duplicateQuery = useQuery({
    queryKey: ["auth", "duplicate-check-pre2fa"],
    queryFn: () => orpcClient.auth.checkDuplicatePre2fa(),
    retry: false,
  });
  const [duplicateAcked, setDuplicateAcked] = useState(false);
  const showDuplicateWarning = !!duplicateQuery.data?.isDuplicate && !duplicateAcked;
  const duplicateResolved = duplicateQuery.isSuccess && !showDuplicateWarning;

  function redirectToTarget() {
    const target = safePostAuthRedirect(redirect_uri, runtimeConfig) ?? "/";
    void navigateToFinalDestination({
      target,
      navigate,
      runtimeConfig,
      skipDuplicateCheck: duplicateResolved,
    });
  }

  const totpForm = useForm({
    defaultValues: { code: "" },
    onSubmit: async ({ value }) => {
      try {
        setError(null);
        const { error: verifyError } = await authClient.twoFactor.verifyTotp({
          code: value.code,
          trustDevice,
        });
        if (verifyError) {
          setError("That code didn't match. Try again — codes refresh every 30 seconds.");
          return;
        }
        redirectToTarget();
      } catch (err) {
        console.error("2FA verification failed:", err);
        setError("Something went wrong. Please try again.");
      }
    },
  });

  const otpForm = useForm({
    defaultValues: { code: "" },
    onSubmit: async ({ value }) => {
      try {
        setError(null);
        const { error: verifyError } = await authClient.twoFactor.verifyOtp({
          code: value.code,
          trustDevice,
        });
        if (verifyError) {
          setError("That code didn't match. Codes expire after a few minutes — try resending.");
          return;
        }
        redirectToTarget();
      } catch (err) {
        console.error("OTP 2FA verification failed:", err);
        setError("Something went wrong. Please try again.");
      }
    },
  });

  function isAuthenticatorOnlyError(err: { code?: string; message?: string }) {
    return (
      err.code === "OTP_DELIVERY_NOT_CONFIGURED" ||
      err.message?.includes("authenticator app") === true
    );
  }

  const sendOtpMutation = useMutation({
    mutationFn: async (_options: { fallbackToTotp?: boolean } = {}) => {
      const { error: sendError } = await authClient.twoFactor.sendOtp();
      if (sendError) throw sendError;
    },
    onMutate: () => {
      setError(null);
    },
    onSuccess: () => {
      setCanUseOtpDelivery(true);
      setOtpSent(true);
      setMode("otp");
    },
    onError: (err, options) => {
      console.error("OTP send failed:", err);
      if (options.fallbackToTotp && isAuthenticatorOnlyError(err)) {
        setCanUseOtpDelivery(false);
        setOtpSent(false);
        setMode("totp");
        setError(null);
        return;
      }
      setError("Something went wrong. Please try again.");
    },
  });

  function sendOtpCode(options: { fallbackToTotp?: boolean } = {}) {
    sendOtpMutation.mutate(options);
  }

  useEffect(() => {
    const dedupeKey = redirect_uri ?? "__default__";
    const now = Date.now();
    const lastAutoSendAt = lastAutoSendAtByKey.get(dedupeKey) ?? 0;
    if (now - lastAutoSendAt < AUTO_SEND_DEDUPE_MS) {
      setCanUseOtpDelivery(true);
      setOtpSent(true);
      setMode("otp");
      return;
    }
    lastAutoSendAtByKey.set(dedupeKey, now);
    sendOtpCode({ fallbackToTotp: true });
  }, [redirect_uri, sendOtpMutation.mutate]);

  const recoveryForm = useForm({
    defaultValues: { code: "" },
    onSubmit: async ({ value }) => {
      try {
        setError(null);
        const { error: verifyError } = await authClient.twoFactor.verifyBackupCode({
          code: value.code,
        });
        if (verifyError) {
          setError("That recovery code didn't work. Each one is single-use — try a different one.");
          return;
        }
        // Recovery code login succeeded — server hook auto-disabled 2FA
        // and flagged session. Full page redirect to security setup so the
        // TanStack Query session cache is cleared and a fresh getSession()
        // picks up the new session cookie.
        const redirectAfterSetup = safePostAuthRedirect(redirect_uri, runtimeConfig) ?? "/";
        navigate({
          to: "/security",
          search: {
            setup: true,
            recovery: true,
            redirect_uri: redirectAfterSetup,
          },
        });
      } catch (err) {
        console.error("Recovery code verification failed:", err);
        setError("Something went wrong. Please try again.");
      }
    },
  });

  const usedAllCodes = error?.includes("used all") || error?.includes("no backup");

  if (mode === "detecting") {
    return (
      <AuthLoading label="Checking verification method…" data-testid="2fa-detecting-loading" />
    );
  }

  return (
    <div className="space-y-6">
      <DuplicateAccountWarningModal
        open={showDuplicateWarning}
        canonicalEmail={duplicateQuery.data?.duplicateOfEmailMasked ?? null}
        onProceed={() => setDuplicateAcked(true)}
        onUseOtherAccount={() => navigate({ to: "/login", search: { redirect_uri } })}
        data-testid="2fa-duplicate-account-dialog"
      />
      <div className="text-center space-y-2">
        {/* Carries the mode — a test reads this to know which challenge it got. */}
        <h2 className="text-2xl font-semibold tracking-tight" data-testid="2fa-title">
          {mode === "totp" && "One more step"}
          {mode === "otp" && "Check your messages"}
          {mode === "recovery" && "Use a recovery code"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {mode === "totp" && "Enter the 6-digit code from your authenticator app."}
          {mode === "otp" &&
            (otpSent
              ? "We sent a 6-digit code using your saved delivery method."
              : "Send a 6-digit code using your saved delivery method.")}
          {mode === "recovery" && "Enter one of the backup codes you saved during setup."}
        </p>
      </div>

      {error && (
        <Alert variant="destructive" data-testid="2fa-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
            {usedAllCodes && (
              <p className="mt-2 text-xs">
                Out of recovery codes?{" "}
                <a
                  href="mailto:support@withvisor.com?subject=Account%20recovery"
                  className="underline underline-offset-2"
                  data-testid="2fa-support-link"
                >
                  Email support
                </a>{" "}
                and we'll help you back in.
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}

      {mode === "totp" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            totpForm.handleSubmit();
          }}
          className="space-y-6"
          data-testid="2fa-totp-form"
        >
          <totpForm.Field
            name="code"
            validators={{
              onChange: z.string().length(6, "Code must be 6 digits").regex(/^\d+$/, "Digits only"),
            }}
            children={(field) => (
              <div className="space-y-2">
                <OtpDigits
                  value={field.state.value}
                  onChange={field.handleChange}
                  data-testid="2fa-totp-code-input"
                />
                {field.state.meta.errors.length > 0 && field.state.value.length > 0 && (
                  <p className="text-sm text-destructive text-center" data-testid="2fa-totp-error">
                    {typeof field.state.meta.errors[0] === "string"
                      ? field.state.meta.errors[0]
                      : field.state.meta.errors[0]?.message}
                  </p>
                )}
              </div>
            )}
          />

          <div className="flex items-center gap-2">
            <Checkbox
              id="trust-device"
              checked={trustDevice}
              onCheckedChange={(v) => setTrustDevice(v === true)}
              data-testid="2fa-trust-device-checkbox"
            />
            <Label htmlFor="trust-device" className="text-sm font-normal cursor-pointer">
              Trust this device for 30 days
            </Label>
          </div>

          <totpForm.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="w-full"
                data-testid="2fa-totp-submit-button"
              >
                {isSubmitting ? "Verifying…" : "Verify"}
              </Button>
            )}
          />
        </form>
      )}

      {mode === "otp" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            otpForm.handleSubmit();
          }}
          className="space-y-6"
          data-testid="2fa-otp-form"
        >
          {!otpSent ? (
            <Button
              type="button"
              onClick={() => sendOtpCode()}
              disabled={sendOtpMutation.isPending}
              className="w-full"
              data-testid="2fa-send-code-button"
            >
              {sendOtpMutation.isPending ? "Sending…" : "Send verification code"}
            </Button>
          ) : (
            <>
              <otpForm.Field
                name="code"
                validators={{
                  onChange: z
                    .string()
                    .length(6, "Code must be 6 digits")
                    .regex(/^\d+$/, "Digits only"),
                }}
                children={(field) => (
                  <div className="space-y-2">
                    <OtpDigits
                      value={field.state.value}
                      onChange={field.handleChange}
                      data-testid="2fa-otp-code-input"
                    />
                    {field.state.meta.errors.length > 0 && field.state.value.length > 0 && (
                      <p
                        className="text-sm text-destructive text-center"
                        data-testid="2fa-otp-error"
                      >
                        {typeof field.state.meta.errors[0] === "string"
                          ? field.state.meta.errors[0]
                          : field.state.meta.errors[0]?.message}
                      </p>
                    )}
                  </div>
                )}
              />

              <div className="flex items-center gap-2">
                <Checkbox
                  id="trust-device-otp"
                  checked={trustDevice}
                  onCheckedChange={(v) => setTrustDevice(v === true)}
                  data-testid="2fa-otp-trust-device-checkbox"
                />
                <Label htmlFor="trust-device-otp" className="text-sm font-normal cursor-pointer">
                  Trust this device for 30 days
                </Label>
              </div>

              <otpForm.Subscribe
                selector={(state) => [state.canSubmit, state.isSubmitting]}
                children={([canSubmit, isSubmitting]) => (
                  <Button
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                    className="w-full"
                    data-testid="2fa-otp-submit-button"
                  >
                    {isSubmitting ? "Verifying…" : "Verify"}
                  </Button>
                )}
              />

              <button
                type="button"
                onClick={() => {
                  setOtpSent(false);
                  otpForm.reset();
                  sendOtpCode();
                }}
                disabled={sendOtpMutation.isPending}
                className="block w-full text-center text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                data-testid="2fa-resend-code-button"
              >
                Didn't get it? Resend
              </button>
            </>
          )}
        </form>
      )}

      {mode === "recovery" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            recoveryForm.handleSubmit();
          }}
          className="space-y-6"
          data-testid="2fa-recovery-form"
        >
          <recoveryForm.Field
            name="code"
            validators={{
              onChange: z.string().min(1, "Enter a recovery code"),
            }}
            children={(field) => (
              <div>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value.trim())}
                  placeholder="abcd-efgh-1234"
                  className="text-lg text-center font-mono tracking-wider"
                  autoFocus
                  data-testid="2fa-recovery-code-input"
                />
                {field.state.meta.errors.length > 0 && (
                  <p
                    className="text-sm text-destructive mt-2 text-center"
                    data-testid="2fa-recovery-error"
                  >
                    {typeof field.state.meta.errors[0] === "string"
                      ? field.state.meta.errors[0]
                      : field.state.meta.errors[0]?.message}
                  </p>
                )}
              </div>
            )}
          />

          <recoveryForm.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="w-full"
                data-testid="2fa-recovery-submit-button"
              >
                {isSubmitting ? "Verifying…" : "Use recovery code"}
              </Button>
            )}
          />
        </form>
      )}

      {/* Method switcher — promoted from a tiny link to a peer row.
          Recovery sits visually apart with its own outline button so a
          locked-out user finds it immediately, not by squinting at links. */}
      <div className="space-y-3 pt-2">
        <div className="flex items-center justify-center gap-4 text-sm">
          {mode !== "totp" && (
            <button
              type="button"
              onClick={() => {
                setMode("totp");
                setError(null);
                setOtpSent(false);
              }}
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              data-testid="2fa-use-authenticator-button"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Authenticator app
            </button>
          )}
          {mode !== "otp" && canUseOtpDelivery && (
            <button
              type="button"
              onClick={() => {
                setMode("otp");
                setError(null);
                if (!otpSent) sendOtpCode();
              }}
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              data-testid="2fa-use-otp-button"
            >
              <Mail className="h-3.5 w-3.5" />
              Send a code
            </button>
          )}
        </div>

        {mode !== "recovery" && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setMode("recovery");
              setError(null);
              setOtpSent(false);
            }}
            className="w-full"
            data-testid="2fa-use-recovery-button"
          >
            <Key className="mr-2 h-4 w-4" />
            Lost access? Use a recovery code
          </Button>
        )}
      </div>
    </div>
  );
}
